import { useState } from "react";
import type { LayerControllerState } from "../../layers/LayerController";
import { LAYER_PRESETS, MAX_RASTER_LAYERS, type LayerPreset } from "../../layers/layerTypes";

interface LayersPanelProps {
  destructiveAllowed?: boolean;
  state: LayerControllerState;
  onActivate: (layerId: string) => void;
  onAdd: (preset: LayerPreset) => void;
  onRename: (layerId: string, name: string) => void;
  onVisible: (layerId: string, visible: boolean) => void;
  onLocked: (layerId: string, locked: boolean) => void;
  onOpacity: (layerId: string, opacity: number) => void;
  onMove: (layerId: string, direction: -1 | 1) => void;
  onDelete: (layerId: string) => void;
  onClear: (layerId: string) => void;
  onMerge: (layerId: string) => void;
  previewUrls: Readonly<Record<string, string>>;
}

export function LayersPanel(props: LayersPanelProps) {
  const destructiveAllowed = props.destructiveAllowed ?? true;
  const [preset, setPreset] = useState<LayerPreset>("ink");
  const [renaming, setRenaming] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [opacityDrafts, setOpacityDrafts] = useState<Record<string, number>>({});
  const ordered = [...props.state.layers].sort((left, right) => right.order - left.order);
  const rasterCount = props.state.layers.filter((layer) => layer.type === "raster").length;

  const commitOpacity = (layerId: string): void => {
    const value = opacityDrafts[layerId];
    if (value !== undefined) {
      props.onOpacity(layerId, value / 100);
      setOpacityDrafts((current) => {
        const next = { ...current };
        delete next[layerId];
        return next;
      });
    }
  };

  return (
    <aside className="layers-panel" aria-label="Layers" data-testid="layers-panel">
      <div className="layers-panel__heading">
        <div>
          <h2>Layers</h2>
          <span>
            {rasterCount}/{MAX_RASTER_LAYERS} drawing layers
          </span>
        </div>
      </div>
      <div className="layers-panel__add">
        <label htmlFor="layer-preset">New layer</label>
        <div>
          <select
            id="layer-preset"
            onChange={(event) => setPreset(event.target.value as LayerPreset)}
            value={preset}
          >
            {LAYER_PRESETS.map((item) => (
              <option key={item.role} value={item.role}>
                {item.label}
              </option>
            ))}
          </select>
          <button
            disabled={rasterCount >= MAX_RASTER_LAYERS}
            onClick={() => props.onAdd(preset)}
            type="button"
          >
            Add
          </button>
        </div>
      </div>
      <ol className="layers-list" aria-label="Layer stack, top layer first">
        {ordered.map((layer, visualIndex) => {
          const active = layer.id === props.state.activeLayerId;
          const modelIndex = props.state.layers.findIndex((candidate) => candidate.id === layer.id);
          const lower = props.state.layers[modelIndex - 1];
          const upper = props.state.layers[modelIndex + 1];
          const crossesBackgroundBoundary = (
            target: (typeof props.state.layers)[number] | undefined,
          ): boolean =>
            target !== undefined &&
            (layer.role === "background") !== (target.role === "background");
          return (
            <li className={`layer-row${active ? " layer-row--active" : ""}`} key={layer.id}>
              <button
                aria-label={`Select ${layer.name} layer`}
                aria-pressed={active}
                className="layer-row__select"
                data-testid={`layer-select-${layer.role}`}
                onClick={() => props.onActivate(layer.id)}
                type="button"
              >
                <span className={`layer-thumb layer-thumb--${layer.type}`} aria-hidden="true">
                  {props.previewUrls[layer.id] ? (
                    <img alt="" src={props.previewUrls[layer.id]} />
                  ) : layer.type === "object" ? (
                    "Aa"
                  ) : (
                    ""
                  )}
                </span>
                <span>
                  <strong>{layer.name}</strong>
                  <small>{layer.type === "object" ? "Objects" : layer.role}</small>
                </span>
              </button>
              <div className="layer-row__toggles">
                <button
                  aria-label={`${layer.visible ? "Hide" : "Show"} ${layer.name}`}
                  aria-pressed={!layer.visible}
                  onClick={() => props.onVisible(layer.id, !layer.visible)}
                  type="button"
                >
                  {layer.visible ? "👁" : "—"}
                </button>
                <button
                  aria-label={`${layer.locked ? "Unlock" : "Lock"} ${layer.name}`}
                  aria-pressed={layer.locked}
                  onClick={() => props.onLocked(layer.id, !layer.locked)}
                  type="button"
                >
                  {layer.locked ? "🔒" : "🔓"}
                </button>
              </div>
              {renaming === layer.id ? (
                <form
                  className="layer-row__rename"
                  onSubmit={(event) => {
                    event.preventDefault();
                    props.onRename(layer.id, name);
                    setRenaming(null);
                  }}
                >
                  <label htmlFor={`rename-${layer.id}`}>Layer name</label>
                  <input
                    autoFocus
                    id={`rename-${layer.id}`}
                    maxLength={40}
                    onChange={(event) => setName(event.target.value)}
                    value={name}
                  />
                  <button type="submit">Done</button>
                  <button onClick={() => setRenaming(null)} type="button">
                    Cancel
                  </button>
                </form>
              ) : null}
              <label className="layer-row__opacity">
                <span>Opacity {opacityDrafts[layer.id] ?? Math.round(layer.opacity * 100)}%</span>
                <input
                  aria-label={`${layer.name} opacity`}
                  disabled={layer.locked}
                  max="100"
                  min="0"
                  onBlur={() => commitOpacity(layer.id)}
                  onChange={(event) =>
                    setOpacityDrafts((current) => ({
                      ...current,
                      [layer.id]: Number(event.target.value),
                    }))
                  }
                  onKeyUp={() => commitOpacity(layer.id)}
                  onPointerUp={() => commitOpacity(layer.id)}
                  step="1"
                  type="range"
                  value={opacityDrafts[layer.id] ?? Math.round(layer.opacity * 100)}
                />
              </label>
              <div className="layer-row__actions">
                <button
                  aria-label={`Move ${layer.name} up`}
                  disabled={layer.locked || visualIndex === 0 || crossesBackgroundBoundary(upper)}
                  onClick={() => props.onMove(layer.id, 1)}
                  type="button"
                >
                  ↑
                </button>
                <button
                  aria-label={`Move ${layer.name} down`}
                  disabled={
                    layer.locked ||
                    visualIndex === ordered.length - 1 ||
                    crossesBackgroundBoundary(lower)
                  }
                  onClick={() => props.onMove(layer.id, -1)}
                  type="button"
                >
                  ↓
                </button>
                <button
                  disabled={layer.system || layer.locked}
                  onClick={() => {
                    setName(layer.name);
                    setRenaming(layer.id);
                  }}
                  type="button"
                >
                  Rename
                </button>
                {layer.type === "raster" ? (
                  <button
                    disabled={layer.locked || !destructiveAllowed}
                    onClick={() => props.onClear(layer.id)}
                    type="button"
                  >
                    Clear
                  </button>
                ) : null}
                {layer.type === "raster" ? (
                  <button
                    disabled={
                      layer.locked ||
                      !destructiveAllowed ||
                      !layer.visible ||
                      lower?.type !== "raster" ||
                      lower.locked ||
                      !lower.visible
                    }
                    onClick={() => props.onMerge(layer.id)}
                    type="button"
                  >
                    Merge ↓
                  </button>
                ) : null}
                <button
                  disabled={
                    !destructiveAllowed ||
                    layer.system ||
                    layer.locked ||
                    layer.type !== "raster" ||
                    rasterCount <= 1
                  }
                  onClick={() => props.onDelete(layer.id)}
                  type="button"
                >
                  Delete
                </button>
              </div>
            </li>
          );
        })}
      </ol>
    </aside>
  );
}
